import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Grid,
  useMediaQuery,
  Checkbox,
  FormControlLabel,
  useTheme,
  Button,
  CircularProgress,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { Images } from "../../utils/Images";
import AuthenticationTextInput from "../../components/common/TextInput/AuthenticationTextInput";
import { DonateButtonVariant } from "../../components/common/Button/Button";
import DonateLeftBanner from "../../components/common/DonateLeftBanner";
import { styled } from "@mui/material/styles";
import BannerSection from "../../components/common/BannerSection";
import CurrencySelectorPopup from "../../components/common/CurrencySelectorPopup"; // Import the popup
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { handleDonate } from "../../utils/Donation";
import { useSelector, useDispatch } from "react-redux";
import StripeEmbeddedCheckout from "../../components/extendable/StripeEmbeddedCheckout";
import { setStripeCheckout, clearStripeCheckout } from "../../store/slices/stripeCheckoutSlice";
import { fetchStripeSummary, waitForFinalStatus } from "../../utils/Stripe";
import { Countries } from "../../utils/GeoLocation/Country";
import { StatesCountry } from "../../utils/GeoLocation/States";
import { validateDonationAmount } from "../../utils/donationValidation";
import {
  sanitizeFormFieldValue,
  validateIndianMobile,
} from "../../utils/phoneValidation";
import { fetchCmsHomeAction } from "../../store/slices/cmsSlice";

const CustomDonateButton = styled(Button)(({ theme, isMobile, isTablet }) => ({
  backgroundColor: "#F04951",
  color: "#FFFFFF",
  borderRadius: "10px",
  boxShadow: "0px 4px 0px #141414",
  // padding: isMobile ? "12px 16px" : isTablet ? "14px 20px" : "15px 24px",
  width: "100%", // 100% width for responsiveness
  height: isMobile ? "44px" : isTablet ? "48px" : "52px",
  fontFamily: "Red Hat Display, sans-serif",
  fontWeight: 700,
  fontSize: isMobile ? "13px" : isTablet ? "14px" : "15px",
  lineHeight: isMobile ? "13px" : isTablet ? "14px" : "15px",
  textAlign: "center",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: isMobile ? "6px" : isTablet ? "7px" : "8px",
  textTransform: "none",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: "#d63f46",
    boxShadow: "0px 4px 0px #141414",
  },
  "&:active": {
    boxShadow: "0px 2px 0px #141414",
    transform: "translateY(2px)",
  },
  "&:disabled": {
    backgroundColor: "#d63f46",
    opacity: 0.7,
    cursor: "not-allowed",
  },
}));

const supportOptions = [
  { label: "12% - ₹360", value: "12" },
  { label: "15% - ₹450", value: "15" },
  { label: "20% - ₹600", value: "20" },
];

const currencyList = [
  { code: "INR", symbol: "₹" },
  { code: "USD", symbol: "$" },
  { code: "AED", symbol: "د.إ" },
  { code: "CAD", symbol: "C$" },
  { code: "AUD", symbol: "A$" },
  { code: "GBP", symbol: "£" },
];

const OverseasDonorPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const cmsHome = useSelector((state) => state.cms.home);
  const { clientSecret: stripeClientSecret, sessionId: stripeSessionId, details: stripeDetails } =
    useSelector((state) => state.stripeCheckout || {});

  // Fetch CMS data if not available
  useEffect(() => {
    if (!cmsHome) {
      dispatch(fetchCmsHomeAction());
    }
  }, [cmsHome, dispatch]);

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const theme = useTheme();
  // Custom media query for screens 900px to 1200px
  const isMediumScreen = useMediaQuery(
    "(min-width: 900px) and (max-width: 1200px)"
  );
  // Custom media query for screens below 900px
  const isSmallScreen = useMediaQuery("(max-width: 899px)");
  // Custom media query for mobile screens (600px and below)
  const isMobile = useMediaQuery("(max-width: 600px)");
  const isAbove1200 = useMediaQuery("(min-width: 1200px)");
  const mobileView = isMobile;

  // Get campaign title, amount, currency, and campaignId from navigation state
  const campaignTitleFromState = location.state?.campaignTitle || "";
  const amountFromState = location.state?.amount;
  const currencyFromState = location.state?.currency;
  const campaignIdFromState = location.state?.campaignId;

  // DonateRightForm state and handlers
  const [supportBy, setSupportBy] = useState("12");
  const [amount, setAmount] = useState(amountFromState ? String(amountFromState) : "1000");
  const [showCurrencyPopup, setShowCurrencyPopup] = useState(false);
  const [currency, setCurrency] = useState(currencyFromState || currencyList[0]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [campaignTitle, setCampaignTitle] = useState(campaignTitleFromState);
  const [campaignId, setCampaignId] = useState(campaignIdFromState);
  const [amountError, setAmountError] = useState(""); // Validation error for amount

  // Update amount, currency, and campaign title when location state changes
  useEffect(() => {
    if (amountFromState) {
      setAmount(String(amountFromState));
    }
    if (currencyFromState) {
      setCurrency(currencyFromState);
    }
    if (campaignTitleFromState) {
      setCampaignTitle(campaignTitleFromState);
    }
    if (campaignIdFromState) {
      setCampaignId(campaignIdFromState);
    }
  }, [amountFromState, currencyFromState, campaignTitleFromState, campaignIdFromState]);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    postal: "",
    country: "",
    countryId: "",
    state: "",
  });
  const [whatsapp, setWhatsapp] = useState(true);
  const [anonymousCheck, setAnonymousCheck] = useState(false);

  // Auto-fill form when user is logged in
  useEffect(() => {
    if (user && user.id) {
      // Only auto-fill if fields are empty and user has the data
      setForm((prevForm) => ({
        ...prevForm,
        name: prevForm.name || user.name || "",
        email: prevForm.email || user.email || "",
        phone: prevForm.phone || user.phone || user.mobile || "",
        // postal code, country, state are not available in user profile, so leave them empty
      }));
    }
  }, [user]);

  // Get states filtered by selected country
  const filteredStates = form.countryId
    ? StatesCountry.filter((state) => state.countryId === parseInt(form.countryId))
    : [];

  const handleInputChange = (field) => (e) => {
    const value = sanitizeFormFieldValue(field, e.target.value);
    setForm({ ...form, [field]: value });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: "" });
    }
  };

  const handleCountryChange = (e) => {
    const countryId = e.target.value;
    const selectedCountry = Countries.find((c) => c.id === parseInt(countryId));
    
    setForm({
      ...form,
      countryId: countryId,
      country: selectedCountry ? selectedCountry.name : "",
      state: "", // Reset state when country changes
    });
    
    // Update currency based on country
    if (selectedCountry) {
      const matchingCurrency = currencyList.find((c) => c.code === selectedCountry.currency);
      if (matchingCurrency) {
        setCurrency(matchingCurrency);
      }
    }
    
    // Clear country error
    if (errors.country) {
      setErrors({ ...errors, country: "" });
    }
  };

  const handleStateChange = (e) => {
    setForm({ ...form, state: e.target.value });
    // Clear state error
    if (errors.state) {
      setErrors({ ...errors, state: "" });
    }
  };

  // Handle Stripe payment completion
  const onStripeComplete = useCallback(async (result) => {
    console.log('onStripeComplete called:', result?.id, stripeSessionId);
    const sessionId = result?.id || stripeSessionId;
    const status = String(result?.status || '').toLowerCase();
    const now = new Date();
    const formattedDate = now.toLocaleDateString('en-US', { day: '2-digit', month: 'long', year: 'numeric' });
    const formattedTime = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    const paymentDateTime = `${formattedDate} | ${formattedTime}`;

    try {
      // Wait until webhook flips DB to success/failed
      const summary = await waitForFinalStatus(sessionId);
      console.log('---in final status', summary, stripeDetails);
      
      const data = {
        paymentId: summary.paymentId || null,
        sessionId: summary.sessionId,
        amount: summary.amount ?? stripeDetails?.amount,
        currency: (summary.currency || stripeDetails?.currency || '').toUpperCase(),
        paymentMethod: 'Stripe',
        paymentDateTime,
        formattedDate,
        formattedTime
      };

      if (summary.status === 'success') {
        console.log('PAYMENT SUCCESSFUL');
        navigate('/thankyou-Page', {
          state: { status: 'Success', data, details: stripeDetails }
        });
      } else {
        console.log('PAYMENT FAILED');
        navigate('/payment-failure', {
          state: { status: 'Failed', data, details: stripeDetails }
        });
      }
    } catch (e) {
      console.log('PAYMENT STATUS CHECK FAILED', e);
      // Webhook didn't finish in time (or fetch failed)
      navigate('/payment-failure', {
        state: {
          status: 'Failed',
          processing: true,
          data: {
            paymentId: null,
            sessionId,
            amount: stripeDetails?.amount,
            currency: stripeDetails?.currency,
            paymentMethod: 'Stripe',
            paymentDateTime,
            formattedDate,
            formattedTime
          },
          details: stripeDetails
        }
      });
    } finally {
      // clear embedded state so you can start again cleanly later
      dispatch(clearStripeCheckout());
    }
  }, [stripeDetails, stripeSessionId, navigate, dispatch]);

  // Clear Stripe checkout on unmount
  useEffect(() => () => dispatch(clearStripeCheckout()), [dispatch]);

  const handleDonateClick = async () => {
    // Validate form
    const validationErrors = {};
    if (!form.name) validationErrors.name = "Full Name is required";
    if (!form.email) validationErrors.email = "Email is required";
    const phoneCheck = validateIndianMobile(form.phone);
    if (!phoneCheck.isValid) validationErrors.phone = phoneCheck.error;
    if (!form.postal) validationErrors.postal = "Postal Code is required";
    if (!form.countryId || !form.country) validationErrors.country = "Country is required";
    // State is optional, but if country has states, validate state
    if (form.countryId && filteredStates.length > 0 && !form.state) {
      validationErrors.state = "State is required";
    }
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      alert("Please fill all required fields");
      return;
    }
    
    // Validate minimum donation amount (overseas payment)
    const validation = validateDonationAmount(amount, cmsHome, true, currency.symbol);
    if (!validation.isValid) {
      setAmountError(validation.error);
      return;
    }
    
    // Clear error if validation passes
    setAmountError("");

    if (loading) return;

    setLoading(true);
    try {
      const additionalData = {
        currency: currency.code,
        postalCode: form.postal,
        whatsappUpdates: whatsapp,
        campaignId: campaignId || "",
        campaignName: campaignTitle || "",
        country: form.country || "",
        anonymousCheck: anonymousCheck ? 1 : 0,
        state: form.state || "",
        donationType: "overseas", // Mark as overseas donation
      };

      const stripeResult = await handleDonate(
        form,
        Number(amount),
        navigate,
        additionalData,
        user?.id,
        null // No snackbar for now
      );

      console.log('Stripe result:', stripeResult);
      console.log('ClientSecret (raw):', stripeResult?.clientSecret);
      console.log('SessionId:', stripeResult?.sessionId);
      
      if (stripeResult?.clientSecret && stripeResult?.sessionId) {
        // Ensure clientSecret is properly decoded
        let decodedClientSecret = stripeResult.clientSecret;
        if (decodedClientSecret && decodedClientSecret.includes('%')) {
          try {
            decodedClientSecret = decodeURIComponent(decodedClientSecret);
            console.log('ClientSecret (decoded):', decodedClientSecret);
          } catch (e) {
            console.warn('Failed to decode clientSecret:', e);
          }
        }
        
        dispatch(setStripeCheckout({
          clientSecret: decodedClientSecret,
          sessionId: stripeResult.sessionId,
          details: {
            ...form,
            ...additionalData,
            userId: user?.id,
            donationType: "overseas",
            amount: Number(amount),
            currency: additionalData.currency,
          }
        }));
      } else {
        console.error('Missing clientSecret or sessionId:', { clientSecret: stripeResult?.clientSecret, sessionId: stripeResult?.sessionId });
        alert("Could not start Stripe checkout. Please try again.");
      }
    } catch (error) {
      console.error("Donation error:", error);
      alert("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  const [citizen, setcitizen] = useState(true);

  // Currency dropdown handlers
const handleCurrencyClick = (event) => {
  if (isMobile) {
    // Show popup on mobile
    setShowCurrencyPopup(true);
  } else {
    // Show dropdown on desktop
    setAnchorEl(event.currentTarget);
  }
};
  const handleCurrencySelect = (cur) => {
    setCurrency(cur);
    setAnchorEl(null);
    // If currency changes to INR, navigate to donate-once-page
    if (cur.code === "INR") {
      navigate("/donate-once-page", {
        state: {
          campaignTitle: campaignTitle,
          campaignId: campaignId,
          amount: amount,
          currency: cur,
        },
      });
    }
  };

  const handleCurrencyPopupSelect = (cur) => {
    setCurrency(cur);
    // If currency changes to INR, navigate to donate-once-page
    if (cur.code === "INR") {
      navigate("/donate-once-page", {
        state: {
          campaignTitle: campaignTitle,
          campaignId: campaignId,
          amount: amount,
          currency: cur,
        },
      });
    }
  };

  const handleCloseCurrencyPopup = () => {
    setShowCurrencyPopup(false);
  };
  return (
    <Box>
      <Box
        sx={{
          mb: "40px",
          mt: "30px",
          display: isSmallScreen ? "block" : "none",
        }}
      >
        <BannerSection title="Overseas Donor" subtitle={false} />
      </Box>

      <Box
        sx={{
          background: isMobile ? "unset" : "rgba(232, 232, 232, 0.40)",
          // minHeight: "100vh",
          p: isMobile ? "0" : isMediumScreen ? "16px" : "30px",
          width: "94.4%",
          maxWidth: "1440px",
          borderRadius: "20px",
          mx: "auto",
        }}
      >
        <Box sx={{ display: "flex", gap: isSmallScreen ? "0" : "24px" }}>
          {/* DonateLeftBanner */}
          <Box>
            <DonateLeftBanner />
          </Box>
          {/* DonateRightForm content */}
          <Box
            sx={{
              background: "#fff",
              borderRadius: isMobile ? "20px" : "20px",
              boxShadow: isMobile ? "none" : "0 4px 24px rgba(0,0,0,0.08)",
              p: isMobile ? "0px" : "30px 26px",
              pb: isSmallScreen ? "80px" : undefined, // breathing room for fixed donate bar on mobile
              minHeight: isMobile ? "auto" : "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              flex: 1,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: isMobile
                    ? "12px"
                    : isMediumScreen
                    ? "14px"
                    : "18px",
                  color: "#1D1D1B",
                  fontWeight: 700,
                  display: isMobile ? "none" : "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: "16px",
                }}
              >
                <Box component="span">
                  <img
                    src={Images.streamlineSharpStar}
                    alt="Orange Sparks"
                    style={{
                      height: "auto",
                      zIndex: 1,
                    }}
                  />
                </Box>
                Overseas Donor
              </Typography>
              {/* Campaign Title - Display before amount field */}
              {campaignTitle && (
                <Typography
                  sx={{
                    fontSize: isMobile
                      ? "12px"
                      : isMediumScreen
                      ? "14px"
                      : "20px",
                    fontWeight: 700,
                    color: "#1D1D1B",
                    mb: isMobile ? 1 : "16px",
                    lineHeight: isMobile ? "20px" : 1.2,
                    letterSpacing: "-0.4px",
                    mt: "16px",
                  }}
                >
                  {campaignTitle}
                </Typography>
              )}
            </Box>

            {/* Currency + Amount compact input */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                background: "#F5F8ED",
                borderRadius: "8px",
                border: "1.114px  solid #BDD188",
                px: 2,
                py: 1,
                minWidth: isMobile ? "120px" : "180px",
                fontWeight: 600,
                fontSize: isMobile ? "15px" : "18px",
                gap: 1,
                mb: 3,
              }}
            >
              {/* Currency selector (custom dropdown) */}
              {/* Currency display (read-only - set from previous page) */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  fontWeight: 700,
                  fontSize: isMobile ? "12px" : "18px",
                  color: "#4A4A49",
                  cursor: "default",
                  pr: 1,
                  minWidth: "55px",
                  borderRight: "0.835px solid #4A4A49",
                }}
              >
                <Box component="span" sx={{ color: "#4A4A49" }}>
                  {currency.symbol}
                </Box>
                <Typography
                  component="span"
                  sx={{
                    ml: 1,
                    color: "#4A4A49",
                    fontWeight: 700,
                    fontSize: isMobile ? "12px" : "17px",
                  }}
                >
                  {currency.code}
                </Typography>
              </Box>
              <Box sx={{ fontWeight: 600, color: "#222", pl: 1, flex: 1 }}>
                <input
                  type="text"
                  value={amount}
                  readOnly
                  style={{
                    border: "none",
                    background: "transparent",
                    fontWeight: 700,
                    fontSize: isMobile ? "12px" : isMobile ? "14px" : "17px",
                    outline: "none",
                    width: "100%",
                    color: "#222",
                    cursor: "default",
                  }}
                />
              </Box>
            </Box>
            {/* Validation error text */}
            {amountError && (
              <Typography
                sx={{
                  color: "#F04951",
                  fontSize: isMobile ? "11px" : "12px",
                  mt: -2,
                  mb: 2,
                  ml: 1,
                }}
              >
                {amountError}
              </Typography>
            )}
            {/* Form fields */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: isMobile ? "auto" : "auto auto",
                columnGap: "27px",
              }}
            >
              <Box>
                <AuthenticationTextInput
                  label="Full Name"
                  value={form.name}
                  onChange={handleInputChange("name")}
                  placeholder="Enter Full Name"
                  required
                  error={!!errors.name}
                  helperText={errors.name}
                />
              </Box>
              <Box>
                <AuthenticationTextInput
                  label="Email ID"
                  value={form.email}
                  onChange={handleInputChange("email")}
                  placeholder="Enter E mail Id"
                  required
                  error={!!errors.email}
                  helperText={errors.email}
                />
              </Box>
              <Box>
                <AuthenticationTextInput
                  label="Phone Number"
                  value={form.phone}
                  onChange={handleInputChange("phone")}
                  placeholder="Enter Phone Number"
                  phoneInput
                  required
                  error={!!errors.phone}
                  helperText={errors.phone}
                />
              </Box>
              {/* Country Dropdown */}
              <Box>
                <Typography
                  sx={{
                    marginBottom: isMobile
                      ? "10px"
                      : isSmallScreen
                      ? "10px"
                      : "12px",
                    fontWeight: 500,
                    fontSize: isMobile
                      ? "14px"
                      : isSmallScreen
                      ? "15px"
                      : "16px",
                    color: "#1D1D1B",
                  }}
                >
                  Country
                </Typography>
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    mb: isMobile ? 2 : 3,
                  }}
                >
                  <select
                    value={form.countryId || ""}
                    onChange={handleCountryChange}
                    style={{
                      width: "100%",
                      padding: "10px",
                      borderRadius: "9px",
                      border: errors.country ? "1px solid #F04951" : "1px solid #B9B9B8",
                      fontSize: isMobile
                        ? "12px"
                        : isSmallScreen
                        ? "13px"
                        : "14px",
                      color: form.countryId ? "#222" : "#979796",
                      background: "#fff",
                      appearance: "none",
                      fontWeight: 500,
                      outline: "none",
                      height: isMobile
                        ? "43px"
                        : isSmallScreen
                        ? "48px"
                        : "52px",
                    }}
                  >
                    <option value="" disabled>
                      Select Country
                    </option>
                    {Countries.map((country) => (
                      <option key={country.id} value={country.id}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                  {errors.country && (
                    <Typography
                      sx={{
                        color: "#F04951",
                        fontSize: "12px",
                        mt: 0.5,
                        ml: 1,
                      }}
                    >
                      {errors.country}
                    </Typography>
                  )}
                  <Box
                    component="img"
                    src={Images.downArrow}
                    alt="Down Arrow"
                    sx={{
                      position: "absolute",
                      right: isMobile ? "12px" : "18px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      width: isMobile ? "20px" : "24px",
                      pointerEvents: "none",
                    }}
                  />
                </Box>
              </Box>
              {/* state Dropdown */}
              <Box>
                <Typography
                  sx={{
                    marginBottom: isMobile
                      ? "10px"
                      : isSmallScreen
                      ? "10px"
                      : "12px",
                    fontWeight: 500,
                    fontSize: isMobile
                      ? "14px"
                      : isSmallScreen
                      ? "15px"
                      : "16px",
                    color: "#1D1D1B",
                  }}
                >
                  State
                </Typography>
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    mb: isMobile ? 2 : 3,
                  }}
                >
                  <select
                    value={form.state || ""}
                    onChange={handleStateChange}
                    disabled={!form.countryId || filteredStates.length === 0}
                    style={{
                      width: "100%",
                      padding: "10px",
                      borderRadius: "9px",
                      border: errors.state ? "1px solid #F04951" : "1px solid #B9B9B8",
                      fontSize: isMobile
                        ? "12px"
                        : isSmallScreen
                        ? "13px"
                        : "14px",
                      color: form.state ? "#222" : "#979796",
                      background: !form.countryId || filteredStates.length === 0 ? "#f5f5f5" : "#fff",
                      appearance: "none",
                      fontWeight: 500,
                      outline: "none",
                      height: isMobile
                        ? "43px"
                        : isSmallScreen
                        ? "48px"
                        : "52px",
                      cursor: !form.countryId || filteredStates.length === 0 ? "not-allowed" : "pointer",
                    }}
                  >
                    <option value="" disabled>
                      {!form.countryId ? "Select Country First" : filteredStates.length === 0 ? "No States Available" : "Select State"}
                    </option>
                    {filteredStates.map((state) => (
                      <option key={state.id} value={state.name}>
                        {state.name}
                      </option>
                    ))}
                  </select>
                  {errors.state && (
                    <Typography
                      sx={{
                        color: "#F04951",
                        fontSize: "12px",
                        mt: 0.5,
                        ml: 1,
                      }}
                    >
                      {errors.state}
                    </Typography>
                  )}
                  <Box
                    component="img"
                    src={Images.downArrow}
                    alt="Down Arrow"
                    sx={{
                      position: "absolute",
                      right: isMobile ? "12px" : "18px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      width: isMobile ? "20px" : "24px",
                      pointerEvents: "none",
                    }}
                  />
                </Box>
              </Box>

              <Box>
                <AuthenticationTextInput
                  label="Postal Code"
                  value={form.postal}
                  onChange={handleInputChange("postal")}
                  placeholder="Enter Postal Code"
                  required
                />
              </Box>
            </Box>

            {/* Anonymous Checkbox */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={anonymousCheck}
                  onChange={(e) => setAnonymousCheck(e.target.checked)}
                  sx={{
                    color: "#f04951",
                    "&.Mui-checked": { color: "#f04951" },
                    p: 0,
                    mr: "10px",
                    width: isMobile ? "16px" : "20px",
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    fontSize: isMobile ? "12px" : "14px",
                    fontWeight: 500,
                    color: "#1D1D1B",
                  }}
                >
                  Make my donation anonymous
                </Typography>
              }
              sx={{ mb: isMobile ? 1 : "14px", mx: "0" }}
            />
            {/* WhatsApp Checkbox */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={whatsapp}
                  onChange={(e) => setWhatsapp(e.target.checked)}
                  sx={{
                    color: "#f04951",
                    "&.Mui-checked": { color: "#f04951" },
                    p: 0,
                    mr: "10px",
                    width: isMobile ? "16px" : "20px",
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    fontSize: isMobile ? "12px" : "14px",
                    fontWeight: 500,
                    color: "#1D1D1B",
                  }}
                >
                  I want to receive transaction and donation updates on WhatsApp
                </Typography>
              }
              sx={{ mb: isMobile ? 1 : "14px", mx: "0" }}
            />
            {/* Checkbox */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={citizen}
                  onChange={(e) => setcitizen(e.target.checked)}
                  sx={{
                    color: "#f04951",
                    "&.Mui-checked": { color: "#f04951" },
                    p: 0,
                    mr: "10px",
                    width: isMobile ? "16px" : "20px",
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    fontSize: isMobile ? "12px" : "14px",
                    fontWeight: 500,
                    color: "#1D1D1B",
                  }}
                >
                  Are you an Indian citizen ?
                </Typography>
              }
              sx={{ mb: isMobile ? 1 : "14px", mx: "0" }}
            />
            {/* Terms */}
            <Typography
              sx={{
                fontSize: isMobile ? "10px" : "14px",
                color: "#1D1D1B",
                mb: isMobile ? 2 : 3,
                fontWeight: 500,
              }}
            >
              By continuing, you are agreeing to{" "}
              <Box
                component={Link}
                to="/terms-conditions"
                sx={{ 
                  color: "#FF5858", 
                  cursor: "pointer",
                  textDecoration: "none",
                  "&:hover": {
                    color: "#FF5858",
                    textDecoration: "none"
                  },
                  "&:active": {
                    color: "#FF5858",
                    textDecoration: "none"
                  },
                  "&:visited": {
                    color: "#FF5858",
                    textDecoration: "none"
                  },
                  "&:focus": {
                    color: "#FF5858",
                    textDecoration: "none"
                  }
                }}
              >
                Terms of Use
              </Box>{" "}
              and{" "}
              <Box
                component={Link}
                to="/privacy-policy"
                sx={{ 
                  color: "#FF5858", 
                  cursor: "pointer",
                  textDecoration: "none",
                  "&:hover": {
                    color: "#FF5858",
                    textDecoration: "none"
                  },
                  "&:active": {
                    color: "#FF5858",
                    textDecoration: "none"
                  },
                  "&:visited": {
                    color: "#FF5858",
                    textDecoration: "none"
                  },
                  "&:focus": {
                    color: "#FF5858",
                    textDecoration: "none"
                  }
                }}
              >
                Privacy Policy
              </Box>
            </Typography>

            {/* Stripe Checkout or Donate Button */}
            {!isSmallScreen && (
              <div style={{ width: "100%" }}>
                {stripeClientSecret ? (
                  <StripeEmbeddedCheckout
                    clientSecret={stripeClientSecret}
                    onComplete={onStripeComplete}
                  />
                ) : (
                  <CustomDonateButton 
                    onClick={handleDonateClick} 
                    isMobile={isMobile}
                    disabled={loading}
                  >
                    {loading ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CircularProgress size={16} sx={{ color: "#FFFFFF" }} />
                        Processing...
                      </Box>
                    ) : (
                      `Donate ${currency.symbol}${amount}`
                    )}
                  </CustomDonateButton>
                )}
              </div>
            )}
          </Box>
        </Box>
      </Box>
       {isSmallScreen && (
        <div
          style={{
            width: "100%",
            position: "fixed",
            backgroundColor: "#fff",
            bottom: 0,
            left: 0,
            padding: "16px 10px",
            boxShadow: " 0 -4px 16px 0 rgba(0, 0, 0, 0.10)",
          }}
        >
          {stripeClientSecret ? (
            <StripeEmbeddedCheckout
              clientSecret={stripeClientSecret}
              onComplete={onStripeComplete}
            />
          ) : (
            <CustomDonateButton 
              onClick={handleDonateClick} 
              isMobile={isMobile}
              disabled={loading}
            >
              {loading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={16} sx={{ color: "#FFFFFF" }} />
                  Processing...
                </Box>
              ) : (
                `Donate ${currency.symbol}${amount}`
              )}
            </CustomDonateButton>
          )}
        </div>
      )}
       {/* Add Currency Selector Popup */}
      <CurrencySelectorPopup
        open={showCurrencyPopup}
        onClose={handleCloseCurrencyPopup}
        selectedCurrency={currency}
        onCurrencySelect={handleCurrencyPopupSelect}
      />
    </Box>
  );
};

export default OverseasDonorPage;
